import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Shield, RefreshCw, Camera, History, RotateCcw, AlertTriangle,
  CheckCircle, Database, Users, FileWarning, ArrowDownToLine, Eye
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/super-admin/safety`;

const STATUS_BADGE = {
  ok: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
  warning: 'bg-amber-500/15 text-amber-300 border-amber-500/40',
  critical: 'bg-rose-500/15 text-rose-300 border-rose-500/40',
  no_baseline: 'bg-slate-500/15 text-slate-300 border-slate-500/40',
};

const ACTION_COLOR = {
  created: 'text-emerald-300',
  deleted: 'text-rose-300',
  demo_provisioned: 'text-violet-300',
  drop_detected: 'text-rose-400 font-bold',
  snapshot_taken: 'text-slate-400',
  restore_performed: 'text-amber-300',
  updated: 'text-sky-300',
  suspended: 'text-amber-300',
  activated: 'text-emerald-300',
};

export default function SuperAdminTenantSafety() {
  const [integrity, setIntegrity] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken') || ''}` });

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [i, s, e] = await Promise.all([
        axios.get(`${API}/integrity-check`, { headers: headers() }),
        axios.get(`${API}/snapshots?limit=60`, { headers: headers() }),
        axios.get(`${API}/events?limit=100`, { headers: headers() }),
      ]);
      setIntegrity(i.data);
      setSnapshots(s.data);
      setEvents(e.data);
    } catch {
      toast.error('Failed to load tenant safety data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const takeSnapshot = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/snapshots/manual`, {}, { headers: headers() });
      if (res.data?.error) toast.error(res.data.error);
      else toast.success(`Snapshot taken — ${res.data.tenant_count} tenants captured`);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Snapshot failed');
    } finally {
      setBusy(false);
    }
  };

  const restoreAll = async (snapshotId) => {
    if (!window.confirm('Restore ALL missing tenants from this snapshot? This adds back tenant records that exist in the snapshot but not in the current DB. Existing tenants are NOT touched.')) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/restore/${snapshotId}/all`, {}, { headers: headers() });
      toast.success(res.data.message);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  const restoreOne = async (snapshotId, tenantId, slug) => {
    if (!window.confirm(`Restore tenant '${slug}' from this snapshot?`)) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/restore/${snapshotId}/${tenantId}`, {}, { headers: headers() });
      toast.success(res.data.message);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Restore failed');
    } finally {
      setBusy(false);
    }
  };

  const viewSnapshot = async (snapshotId) => {
    try {
      const res = await axios.get(`${API}/snapshots/${snapshotId}`, { headers: headers() });
      setSelectedSnapshot(res.data);
    } catch {
      toast.error('Failed to load snapshot details');
    }
  };

  return (
    <div className="space-y-6" data-testid="tenant-safety-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Tenant Safety
          </h1>
          <p className="text-sm text-slate-400 mt-1 max-w-2xl">
            Auto-snapshots, audit log, and one-click restore — your safety net against accidental tenant loss.
            A snapshot is captured on every backend startup and every 24 hours.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={refresh} disabled={loading} className="border-slate-700 text-slate-200" data-testid="refresh-safety-btn">
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button onClick={takeSnapshot} disabled={busy} className="bg-violet-600 hover:bg-violet-700 text-white font-bold" data-testid="manual-snapshot-btn">
            <Camera className="h-4 w-4 mr-2" />
            Take Snapshot Now
          </Button>
        </div>
      </div>

      {/* Integrity status */}
      <div className={`border rounded-lg p-5 ${STATUS_BADGE[integrity?.status] || 'bg-slate-900 border-slate-800'}`} data-testid="integrity-status">
        <div className="flex items-start gap-3">
          {integrity?.status === 'critical' ? (
            <FileWarning className="h-6 w-6 text-rose-400 flex-shrink-0 mt-0.5" />
          ) : integrity?.status === 'warning' ? (
            <AlertTriangle className="h-6 w-6 text-amber-400 flex-shrink-0 mt-0.5" />
          ) : integrity?.status === 'ok' ? (
            <CheckCircle className="h-6 w-6 text-emerald-400 flex-shrink-0 mt-0.5" />
          ) : (
            <Shield className="h-6 w-6 text-slate-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <h2 className="font-bold text-white text-base mb-1">Data Integrity Check</h2>
            <p className="text-sm text-slate-300">{integrity?.message || 'Loading...'}</p>
            {integrity && integrity.status !== 'no_baseline' && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Current</p>
                  <p className="text-xl font-bold text-white">{integrity.current_count}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Last Snapshot</p>
                  <p className="text-xl font-bold text-white">{integrity.previous_count}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Change</p>
                  <p className={`text-xl font-bold ${integrity.diff < 0 ? 'text-rose-300' : integrity.diff > 0 ? 'text-emerald-300' : 'text-white'}`}>
                    {integrity.diff > 0 ? '+' : ''}{integrity.diff}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">% Change</p>
                  <p className="text-xl font-bold text-white">{integrity.pct_change}%</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Snapshots */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5" data-testid="snapshots-card">
        <div className="flex items-center gap-2 mb-4">
          <Database className="h-5 w-5 text-violet-400" />
          <h2 className="font-bold text-white text-base">Snapshots ({snapshots.length})</h2>
        </div>
        {snapshots.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-6">No snapshots yet. Click "Take Snapshot Now" to create the first one.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-800">
                  <th className="py-2 font-medium">Taken At</th>
                  <th className="py-2 font-medium">Reason</th>
                  <th className="py-2 font-medium">Tenants</th>
                  <th className="py-2 font-medium">Admins</th>
                  <th className="py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {snapshots.map(s => (
                  <tr key={s.id} className="border-b border-slate-800/60" data-testid={`snapshot-row-${s.id}`}>
                    <td className="py-2 text-slate-300 whitespace-nowrap text-xs">{new Date(s.taken_at).toLocaleString()}</td>
                    <td className="py-2 text-slate-400 text-xs">{s.reason}</td>
                    <td className="py-2 text-white font-mono">{s.tenant_count}</td>
                    <td className="py-2 text-white font-mono">{s.super_admin_count}</td>
                    <td className="py-2 text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => viewSnapshot(s.id)} className="border-slate-700 text-slate-300 text-xs" data-testid={`view-snapshot-${s.id}`}>
                        <Eye className="h-3 w-3 mr-1" /> View
                      </Button>
                      <Button size="sm" onClick={() => restoreAll(s.id)} disabled={busy} className="bg-amber-600 hover:bg-amber-700 text-xs" data-testid={`restore-all-${s.id}`}>
                        <ArrowDownToLine className="h-3 w-3 mr-1" /> Restore Missing
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Events */}
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5" data-testid="events-card">
        <div className="flex items-center gap-2 mb-4">
          <History className="h-5 w-5 text-violet-400" />
          <h2 className="font-bold text-white text-base">Audit Trail ({events.length})</h2>
        </div>
        {events.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-6">No events recorded yet. Tenant lifecycle actions will appear here.</p>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-900">
                <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-800">
                  <th className="py-2 font-medium">When</th>
                  <th className="py-2 font-medium">Action</th>
                  <th className="py-2 font-medium">Tenant</th>
                  <th className="py-2 font-medium">By</th>
                </tr>
              </thead>
              <tbody>
                {events.map(e => (
                  <tr key={e.id} className="border-b border-slate-800/40">
                    <td className="py-2 text-slate-400 text-xs whitespace-nowrap">{new Date(e.timestamp).toLocaleString()}</td>
                    <td className={`py-2 font-semibold text-xs ${ACTION_COLOR[e.action] || 'text-slate-300'}`}>{e.action}</td>
                    <td className="py-2 text-slate-300 text-xs">{e.slug || (e.tenant_id ? e.tenant_id.slice(0, 8) : '—')}</td>
                    <td className="py-2 text-slate-500 text-xs break-all">{e.actor}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Snapshot detail dialog */}
      <Dialog open={!!selectedSnapshot} onOpenChange={(o) => !o && setSelectedSnapshot(null)}>
        <DialogContent className="max-w-3xl bg-slate-900 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-white">Snapshot Detail</DialogTitle>
            <DialogDescription className="text-slate-400">
              Taken {selectedSnapshot && new Date(selectedSnapshot.taken_at).toLocaleString()} — {selectedSnapshot?.tenant_count} tenants
            </DialogDescription>
          </DialogHeader>
          {selectedSnapshot && (
            <div className="max-h-[60vh] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-slate-900">
                  <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-800">
                    <th className="py-2 font-medium">Slug</th>
                    <th className="py-2 font-medium">Name</th>
                    <th className="py-2 font-medium">Plan</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSnapshot.tenants?.map(t => (
                    <tr key={t.id} className="border-b border-slate-800/40">
                      <td className="py-2 text-violet-300 font-mono text-xs">{t.slug}</td>
                      <td className="py-2 text-slate-300 text-xs">{t.name}</td>
                      <td className="py-2 text-slate-400 text-xs">{t.plan}</td>
                      <td className="py-2 text-slate-400 text-xs">{t.status}</td>
                      <td className="py-2 text-right">
                        <Button size="sm" variant="outline" onClick={() => restoreOne(selectedSnapshot.id, t.id, t.slug)} disabled={busy} className="border-amber-500/40 text-amber-300 text-xs">
                          <RotateCcw className="h-3 w-3 mr-1" /> Restore
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
